/**
 * Real connectivity detection (heartbeat).
 *
 * Replaces `navigator.onLine` (which is true on "lie-fi": WiFi connected
 * with no real internet) with an active ping to Supabase.
 *
 * Emits `app:online` / `app:offline` on window when state transitions.
 * Use `getIsOnline()` for synchronous reads and `subscribeConnectivity()`
 * or `useConnectivity()` to react to changes.
 */
import { supabase } from "@/integrations/supabase/client";

const PING_INTERVAL_MS = 10_000;
const PING_TIMEOUT_MS = 3_000;

let currentOnline: boolean = typeof navigator !== "undefined" ? navigator.onLine : true;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
let started = false;
let lastOnlineTransitionAt: number = 0;

function emit(next: boolean) {
  if (next === currentOnline) return;
  currentOnline = next;
  if (next) lastOnlineTransitionAt = Date.now();
  try {
    window.dispatchEvent(new Event(next ? "app:online" : "app:offline"));
  } catch {
    /* ignore */
  }
  // eslint-disable-next-line no-console
  console.log(`[connectivity] ${next ? "🟢 online" : "🔴 offline"}`);
}

async function ping(): Promise<boolean> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;
  if (inFlight) return currentOnline;
  inFlight = true;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    try {
      const { error } = await supabase
        .from("restaurants")
        .select("id")
        .limit(1)
        .abortSignal(ctrl.signal);
      clearTimeout(t);
      // PostgREST returned a response (even a row-level error counts as "reachable")
      if (!error) return true;
      // Network/abort errors usually surface here too; treat AbortError as offline
      const msg = (error as { message?: string }).message || "";
      if (/abort|network|fetch/i.test(msg)) return false;
      return true;
    } catch (err) {
      clearTimeout(t);
      return false;
    }
  } finally {
    inFlight = false;
  }
}

async function checkAndEmit() {
  // Skip while tab is hidden — saves battery; will re-check on visibilitychange
  if (typeof document !== "undefined" && document.hidden) return;
  const next = await ping();
  emit(next);
}

export function startHeartbeat() {
  if (started || typeof window === "undefined") return;
  started = true;

  // Initial check
  void checkAndEmit();

  window.addEventListener("online", () => void checkAndEmit());
  window.addEventListener("offline", () => emit(false));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void checkAndEmit();
  });

  pingTimer = setInterval(() => void checkAndEmit(), PING_INTERVAL_MS);
}

export function stopHeartbeat() {
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
  started = false;
}

export function getIsOnline(): boolean {
  return currentOnline;
}

/**
 * Returns the timestamp (ms) of the last offline→online transition.
 * Used by consumers (e.g. phantom-clear guards) to grant a grace
 * window after reconnect for the sync queue to drain.
 */
export function getLastOnlineTransitionAt(): number {
  return lastOnlineTransitionAt;
}

export function subscribeConnectivity(cb: (online: boolean) => void): () => void {
  const onOnline = () => cb(true);
  const onOffline = () => cb(false);
  window.addEventListener("app:online", onOnline);
  window.addEventListener("app:offline", onOffline);
  return () => {
    window.removeEventListener("app:online", onOnline);
    window.removeEventListener("app:offline", onOffline);
  };
}

/** Force an immediate connectivity check (useful after a failed request). */
export function recheckConnectivity(): Promise<void> {
  return checkAndEmit();
}